import { useState } from "react";
import { Icon } from "@common/index";
import { Button } from "@common/Button";
import { ComponentColor, ComponentSize } from "@common/types";
import type { OptionResponse } from "@models/option";
import { ConfirmDeleteDialog } from "./ConfirmDeleteDialog";

interface OptionListProps {
    options: OptionResponse[];
    selectedOptionId: number | null;
    isProcessing: boolean;
    /** Id of the option whose delete is pending confirmation, if any. */
    pendingDeleteId: number | null;
    onSelect: (optionId: number) => void;
    onCreate: (name: string) => void;
    onRename: (optionId: number, name: string) => void;
    onRequestDelete: (option: OptionResponse) => void;
    onConfirmDelete: () => void;
    onCancelDelete: () => void;
}

export const OptionList = ({
    options,
    selectedOptionId,
    isProcessing,
    pendingDeleteId,
    onSelect,
    onCreate,
    onRename,
    onRequestDelete,
    onConfirmDelete,
    onCancelDelete,
}: OptionListProps) => {
    const [newName, setNewName] = useState("");
    const [editingId, setEditingId] = useState<number | null>(null);
    const [editName, setEditName] = useState("");

    const startEditing = (option: OptionResponse) => {
        setEditingId(option.id);
        setEditName(option.name);
    };

    const commitEdit = () => {
        const trimmed = editName.trim();
        if (editingId !== null && trimmed) {
            onRename(editingId, trimmed);
        }
        setEditingId(null);
    };

    const handleAdd = () => {
        const trimmed = newName.trim();
        if (!trimmed) return;
        onCreate(trimmed);
        setNewName("");
    };

    return (
        <div className="flex h-full flex-col">
            <ul className="flex-1 space-y-1 overflow-y-auto">
                {options.map((option) => (
                    <li key={option.id}>
                        {editingId === option.id ? (
                            <input
                                aria-label={`Rename ${option.name}`}
                                className="input input-sm input-bordered w-full text-neutral"
                                value={editName}
                                autoFocus
                                onChange={(e) => setEditName(e.target.value)}
                                onBlur={commitEdit}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") commitEdit();
                                    if (e.key === "Escape") {
                                        // Claimed: Escape cancels the rename,
                                        // not the whole Manage Options modal.
                                        e.preventDefault();
                                        setEditingId(null);
                                    }
                                }}
                            />
                        ) : (
                            <div
                                className={`group flex items-center justify-between rounded px-2 py-1 ${
                                    selectedOptionId === option.id
                                        ? "bg-base-200"
                                        : ""
                                }`}
                            >
                                <button
                                    type="button"
                                    className="flex-1 text-left text-primary"
                                    aria-pressed={
                                        selectedOptionId === option.id
                                    }
                                    onClick={() => onSelect(option.id)}
                                >
                                    {option.name}
                                </button>
                                <span className="flex gap-1">
                                    <button
                                        type="button"
                                        aria-label={`Edit ${option.name}`}
                                        className="btn btn-ghost btn-xs text-neutral"
                                        onClick={() => startEditing(option)}
                                    >
                                        <Icon name="edit" />
                                    </button>
                                    <button
                                        type="button"
                                        aria-label={`Delete ${option.name}`}
                                        className="btn btn-ghost btn-xs text-neutral"
                                        onClick={() => onRequestDelete(option)}
                                    >
                                        <Icon name="delete" />
                                    </button>
                                </span>
                            </div>
                        )}
                        {pendingDeleteId === option.id && (
                            <div className="mt-1">
                                <ConfirmDeleteDialog
                                    label={`Option "${option.name}"`}
                                    isProcessing={isProcessing}
                                    onConfirm={onConfirmDelete}
                                    onCancel={onCancelDelete}
                                />
                            </div>
                        )}
                    </li>
                ))}
            </ul>
            <div className="mt-3 border-t border-base-200 pt-3">
                <label
                    htmlFor="new-option-name"
                    className="mb-1 block text-sm text-neutral"
                >
                    Add New Option
                </label>
                <div className="flex gap-2">
                    <input
                        id="new-option-name"
                        className="input input-sm input-bordered flex-1 text-neutral"
                        placeholder="Name"
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") handleAdd();
                        }}
                    />
                    <Button
                        color={ComponentColor.Primary}
                        size={ComponentSize.SM}
                        loading={isProcessing}
                        onClick={handleAdd}
                    >
                        Add
                    </Button>
                </div>
            </div>
        </div>
    );
};
